/**
 * §14.6 / F-063 — the celebration when a session ends.
 *
 * Two bursts from the bottom corners, as docs/ux-mockup.html fires them,
 * repeating every few seconds while the final standings are up. Shared by the
 * projector and the player device: the person who just won should see it in
 * their hand, not only on the wall. Pieces are
 * plain divs driven by one keyframe and removed when they land, so nothing
 * accumulates on a projector that stays on the results screen all afternoon.
 *
 * Two ways in, because the finale needs both. `useConfetti` is the standing
 * celebration that runs for as long as a screen is up. `fireConfetti` is a
 * single burst on demand, which is what the podium reveal wants: one throw per
 * placement, timed to the card landing rather than to a metronome the reveal
 * would drift against.
 */

import { useEffect } from "react";

const COLORS = ["#f6c453", "#22c55e", "#38bdf8", "#e5222e", "#8b5cf6", "#ffffff", "#fb923c"];
const PIECES_PER_SIDE = 90;
const REPEAT_MS = 3000;
/** How long a piece can stay up before it is swept regardless of animation. */
const PIECE_LIFE_MS = 3200;

/** The projector fires from its stage; a phone fires from its own screen. */
function findStage(): HTMLElement | null {
  const host = document.querySelector(".tq-stage") ?? document.querySelector(".tq-player");
  return host instanceof HTMLElement ? host : null;
}

function reducedMotion(): boolean {
  // §29.2 — a room that asked for less motion does not get thrown paper.
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/**
 * One throw. Pieces sweep themselves, so a caller that fires and forgets — the
 * podium reveal does exactly that — leaves nothing behind. `onPiece` lets the
 * hook below also track them, so unmounting mid-flight clears the screen.
 */
function spawnBurst(stage: HTMLElement, onPiece?: (piece: HTMLElement, timer: number) => void) {
  for (let side = 0; side < 2; side += 1) {
    const fromLeft = side === 0;
    for (let i = 0; i < PIECES_PER_SIDE; i += 1) {
      const piece = document.createElement("div");
      piece.className = "tq-confetti";
      piece.style.left = `${fromLeft ? Math.random() * 4 : 96 + Math.random() * 4}%`;
      piece.style.top = `${85 + Math.random() * 15}%`;
      piece.style.background = COLORS[Math.floor(Math.random() * COLORS.length)]!;
      piece.style.width = `${5 + Math.random() * 5}px`;
      piece.style.height = `${9 + Math.random() * 8}px`;
      piece.style.setProperty("--dx", `${(fromLeft ? 1 : -1) * (90 + Math.random() * 220)}px`);
      piece.style.setProperty("--dy", `${-(160 + Math.random() * 260)}px`);
      piece.style.setProperty("--rot", `${Math.random() * 720 - 360}deg`);
      piece.style.animationDuration = `${1.3 + Math.random() * 1.1}s`;
      piece.style.animationDelay = `${Math.random() * 0.4}s`;
      stage.appendChild(piece);
      const id = window.setTimeout(() => piece.remove(), PIECE_LIFE_MS);
      onPiece?.(piece, id);
    }
  }
}

/**
 * A single burst, for a moment worth marking on its own. Safe to call when the
 * stage is not mounted or the room asked for less motion — it simply does
 * nothing, so callers do not have to guard.
 */
export function fireConfetti(): void {
  if (reducedMotion()) return;
  const stage = findStage();
  if (!stage) return;
  spawnBurst(stage);
}

export function useConfetti(active: boolean): void {
  useEffect(() => {
    if (!active) return;
    if (reducedMotion()) return;
    const stage = findStage();
    if (!stage) return;

    const timers = new Set<number>();
    const live = new Set<HTMLElement>();
    const burst = () =>
      spawnBurst(stage, (piece, timer) => {
        live.add(piece);
        timers.add(timer);
      });

    burst();
    const repeat = setInterval(burst, REPEAT_MS);

    return () => {
      clearInterval(repeat);
      for (const id of timers) clearTimeout(id);
      for (const piece of live) piece.remove();
    };
  }, [active]);
}
